import { useMemo, useState } from "react";

import { Link, useLocation } from "react-router-dom";

import AppBar from "@mui/material/AppBar";
import Card from "@mui/material/Card";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";

import MKBox from "components/MKBox";
import MKButton from "components/MKButton";
import MKTypography from "components/MKTypography";
import DefaultFooter from "examples/Footers/DefaultFooter";
import DefaultNavbar from "examples/Navbars/DefaultNavbar";

import footerRoutes from "footer.routes";
import routes from "routes";
import FaqAccordionList from "studio-edifique/components/FaqAccordionList";
import ProjectGalleryDialog from "studio-edifique/components/ProjectGalleryDialog";
import ProjectPreviewCard from "studio-edifique/components/ProjectPreviewCard";
import SectionHeading from "studio-edifique/components/SectionHeading";
import SeoHead from "studio-edifique/components/SeoHead";
import {
  company,
  createBreadcrumbSchema,
  createFaqSchema,
  createOrganizationSchema,
  heroImages,
  getMobileBackgroundImage,
  portfolioCategories,
} from "studio-edifique/siteContent";

const portfolioFaqs = [
  {
    question: "É possível contratar apenas uma etapa do serviço?",
    answer:
      "Sim. O atendimento pode ser concentrado em projeto, interiores, fachadas, laudos, fiscalização ou administração de obra, conforme a necessidade.",
  },
  {
    question: "Os projetos do portfólio representam escopos completos?",
    answer:
      "Cada recorte mostra um tipo de entrega. Alguns trabalhos destacam concepção arquitetônica, outros interiores, fachadas ou acompanhamento técnico de obra.",
  },
  {
    question: "A Studio Edifique trabalha com projetos residenciais e comerciais?",
    answer:
      "Sim. O portfólio inclui soluções residenciais, comerciais e registros de administração de obra em diferentes contextos.",
  },
];

function PortfolioPage() {
  const { pathname } = useLocation();
  const [activeCategory, setActiveCategory] = useState(portfolioCategories[0].id);
  const [selectedProject, setSelectedProject] = useState(null);

  const currentCategory = useMemo(
    () =>
      portfolioCategories.find((category) => category.id === activeCategory) ||
      portfolioCategories[0],
    [activeCategory]
  );

  return (
    <>
      <SeoHead
        title="Portfólio de Arquitetura, Interiores e Obras"
        description="Veja projetos arquitetônicos, interiores, fachadas e administração de obras da Studio Edifique em Itatiba."
        keywords={[...company.seoKeywords, "portfólio de arquitetura", "projetos Studio Edifique"]}
        path={pathname}
        image={heroImages.portfolio}
        responsiveBackground
        schema={[
          createOrganizationSchema(),
          createBreadcrumbSchema([
            { name: "Início", path: "/" },
            { name: "Portfólio", path: pathname },
          ]),
          createFaqSchema(portfolioFaqs),
        ]}
      />
      <DefaultNavbar
        brand={company.brandName}
        routes={routes}
        action={{
          type: "external",
          route: company.whatsappUrl,
          label: "Solicitar proposta",
          color: "info",
        }}
        transparent
        light
      />
      <MKBox
        minHeight="56vh"
        width="100%"
        sx={{
          backgroundImage: ({ functions: { linearGradient, rgba } }) =>
            `${linearGradient(rgba("#1C1A1A", 0.85), rgba("#111111", 0.56))}, url(${
              heroImages.portfolio
            })`,
          backgroundSize: "cover",
          "@media (max-width: 600px)": {
            backgroundImage: ({ functions: { linearGradient, rgba } }) =>
              `${linearGradient(
                rgba("#1C1A1A", 0.85),
                rgba("#111111", 0.56)
              )}, url(${getMobileBackgroundImage(heroImages.portfolio)})`,
          },
          backgroundPosition: "center",
          display: "grid",
          placeItems: "center",
        }}
      >
        <Container>
          <Grid
            container
            item
            xs={12}
            lg={8}
            justifyContent="center"
            sx={{ mx: "auto", textAlign: "center" }}
          >
            <MKTypography variant="button" textTransform="uppercase" letterSpacing={2} color="info">
              Portfólio de Projetos
            </MKTypography>
            <MKTypography
              variant="h2"
              component="h1"
              color="white"
              sx={({ breakpoints, typography: { size } }) => ({
                mt: 1,
                [breakpoints.down("md")]: {
                  fontSize: size["3xl"],
                },
              })}
            >
              Arquitetura, interiores, fachadas e acompanhamento técnico em um só lugar.
            </MKTypography>
            <MKTypography variant="body1" color="white" opacity={0.84} mt={2}>
              Cada recorte do portfólio ajuda a entender como a Studio Edifique trabalha projeto,
              visualização e execução com leitura prática de escopo.
            </MKTypography>
          </Grid>
        </Container>
      </MKBox>
      <Card
        sx={{
          p: 2,
          mx: { xs: 2, lg: 3 },
          mt: -8,
          mb: 4,
          backgroundColor: ({ palette: { white }, functions: { rgba } }) => rgba(white.main, 0.82),
          backdropFilter: "saturate(180%) blur(20px)",
          boxShadow: ({ boxShadows: { xxl } }) => xxl,
          overflow: "hidden",
        }}
      >
        <MKBox component="section" py={6}>
          <Container>
            <SectionHeading
              eyebrow="Galeria interativa"
              title="Escolha uma categoria e abra cada projeto para ver a galeria completa"
              description="Conheça estudos de arquitetura e interiores e registros de acompanhamento de obra. As páginas dos projetos identificam o material apresentado e o escopo de atuação."
            />
            <AppBar
              position="static"
              sx={{
                mb: 4,
                backgroundColor: "transparent",
                boxShadow: "none",
              }}
            >
              <Tabs
                value={activeCategory}
                onChange={(_, value) => setActiveCategory(value)}
                variant="scrollable"
                scrollButtons="auto"
                textColor="inherit"
                indicatorColor="primary"
              >
                {portfolioCategories.map((category) => (
                  <Tab key={category.id} value={category.id} label={category.label} />
                ))}
              </Tabs>
            </AppBar>

            <Grid container spacing={4} alignItems="center" mb={2}>
              <Grid item xs={12} lg={8}>
                <MKTypography variant="h3">{currentCategory.label}</MKTypography>
                <MKTypography variant="body2" color="text" mt={1}>
                  {currentCategory.description}
                </MKTypography>
              </Grid>
              <Grid item xs={12} lg={4}>
                <MKBox
                  p={2.5}
                  borderRadius="xl"
                  sx={{
                    background: ({ palette: { grey } }) => grey[100],
                  }}
                >
                  <MKTypography variant="button" textTransform="uppercase" color="text">
                    Projetos disponíveis
                  </MKTypography>
                  <MKTypography variant="h3" color="info" mt={1}>
                    {currentCategory.projects.length}
                  </MKTypography>
                </MKBox>
              </Grid>
            </Grid>

            <Grid container spacing={4}>
              {currentCategory.projects.map((project) => (
                <Grid item xs={12} md={6} lg={4} key={project.title}>
                  <ProjectPreviewCard
                    image={project.cover}
                    title={project.title}
                    description={project.description}
                    categories={project.highlights}
                    path={project.path}
                    onAction={() => setSelectedProject(project)}
                  />
                </Grid>
              ))}
            </Grid>
          </Container>
        </MKBox>

        <MKBox
          component="section"
          py={8}
          sx={{
            background: ({ palette: { grey } }) => grey[100],
            mx: -2,
            px: { xs: 2, lg: 0 },
          }}
        >
          <Container>
            <Grid container spacing={6}>
              <Grid item xs={12} lg={5}>
                <SectionHeading
                  eyebrow="Como contratar"
                  title="O portfólio mostra o tipo de entrega. O atendimento define o escopo ideal."
                  description="Nem todo cliente precisa do pacote completo. O primeiro contato serve para entender a demanda e indicar a solução mais adequada."
                />
                <MKButton component={Link} to="/contato" variant="gradient" color="info">
                  Pedir atendimento
                </MKButton>
              </Grid>
              <Grid item xs={12} lg={7}>
                <FaqAccordionList items={portfolioFaqs} />
              </Grid>
            </Grid>
          </Container>
        </MKBox>

        <MKBox component="section" py={8}>
          <Container>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} lg={8}>
                <SectionHeading
                  eyebrow="Pronto para avançar"
                  title="Se o seu projeto precisa de clareza, o próximo passo é conversar com a equipe."
                  description="Envie sua demanda e receba orientação inicial sobre escopo, prioridade e formato de atendimento."
                />
              </Grid>
              <Grid item xs={12} lg={4}>
                <MKBox display="flex" flexDirection="column" gap={1.5}>
                  <MKButton
                    component="a"
                    href={company.whatsappUrl}
                    target="_blank"
                    rel="noreferrer"
                    variant="gradient"
                    color="info"
                  >
                    WhatsApp
                  </MKButton>
                  <MKButton component={Link} to="/blog" variant="outlined" color="dark">
                    Ler conteúdos técnicos
                  </MKButton>
                </MKBox>
              </Grid>
            </Grid>
          </Container>
        </MKBox>
      </Card>
      <ProjectGalleryDialog
        project={selectedProject}
        open={Boolean(selectedProject)}
        onClose={() => setSelectedProject(null)}
      />
      <MKBox pt={6} px={1} mt={6}>
        <DefaultFooter content={footerRoutes} />
      </MKBox>
    </>
  );
}

export default PortfolioPage;
